/**
 * One shared server-sent events stream per tab (/api/events). The app shell and
 * netplay subscribe to it; it stays open while anyone listens (netplay keeps it
 * alive on the player page, which lives outside the shell).
 */
type Listener = (data: unknown) => void;

const listeners = new Map<string, Set<Listener>>();
let source: EventSource | null = null;
let retry: ReturnType<typeof setTimeout> | undefined;
let users = 0;

function attach(es: EventSource, type: string) {
  es.addEventListener(type, (e) => {
    let data: unknown;
    try {
      data = JSON.parse((e as MessageEvent).data);
    } catch {
      return;
    }
    for (const fn of listeners.get(type) ?? []) fn(data);
  });
}

function connect() {
  clearTimeout(retry);
  source = new EventSource('/api/events');
  for (const type of listeners.keys()) attach(source, type);
  source.onerror = () => {
    source?.close();
    source = null;
    if (users > 0) retry = setTimeout(connect, 5_000);
  };
}

/** Listen to one event type; returns the unsubscribe function. */
export function onLive(type: string, fn: Listener): () => void {
  let set = listeners.get(type);
  if (!set) {
    listeners.set(type, (set = new Set()));
    if (source) attach(source, type);
  }
  set.add(fn);
  users++;
  if (!source) connect();
  return () => {
    set!.delete(fn);
    users--;
    if (users === 0) {
      clearTimeout(retry);
      source?.close();
      source = null;
    }
  };
}
